import type {
  EnvelopeStatus,
  FieldType
} from "../../../containers/Esign/types/esign.types.ts"
import type { FieldRect, PageGeometry } from "../geometry.ts"

export type MarkKind = "signature" | "initials"

export type SigningDocumentKind = "subject" | "notice_of_cancellation"
export type MarkMethod = "drawn" | "typed"

export interface SigningField extends FieldRect {
  id: string
  field_type: FieldType
  required: boolean
  label: string
  completed: boolean
  value: string
  /** Absent from a server older than the field, where every field is on the document itself. */
  document?: SigningDocumentKind
}

/** The notice of cancellation a customer is sent beside a home-improvement contract. */
export interface SigningNotice {
  name: string
  page_count: number
  pages: PageGeometry[]
}

// Mirrors the refusal codes apps.esign.signing gives as may_sign_reason in ezbuild_back.
export const MAY_SIGN_REASONS = [
  "not_a_signer",
  "already_signed",
  "not_awaiting_signatures",
  "not_consented",
  "not_your_turn"
] as const

export type MaySignReason = (typeof MAY_SIGN_REASONS)[number]

export interface SigningState {
  page_count: number
  pages: PageGeometry[]
  may_sign: boolean
  /** Absent from a server older than the field. */
  may_sign_reason?: MaySignReason | null
  /** Absent from a server older than the field. */
  envelope_status?: EnvelopeStatus
  adopted: Record<MarkKind, boolean>
  fields: SigningField[]
  /** Absent from a server older than the field; null when nothing is sent beside the document. */
  notice?: SigningNotice | null
}

const CLOSED_STATUSES = ["declined", "voided", "expired"] as const

export type ClosedStatus = (typeof CLOSED_STATUSES)[number]

/** An envelope that ended without everyone signing: it stays readable, never signable. */
export const isClosedStatus = (
  status: EnvelopeStatus | undefined
): status is ClosedStatus =>
  CLOSED_STATUSES.some((closed) => closed === status)

export const closedStatusOf = (state: SigningState): ClosedStatus | null =>
  isClosedStatus(state.envelope_status) ? state.envelope_status : null

export const signingIsOpen = (state: SigningState) =>
  state.may_sign && closedStatusOf(state) === null

/** Completed is when the certificate of completion exists. */
export const hasCertificate = (status: EnvelopeStatus | undefined) =>
  status === "completed"

export const certificateName = (documentName: string) =>
  `${documentName.replace(/\.pdf$/i, "")} - Certificate of Completion.pdf`

export type Outcome<T> =
  | { ok: true; status: number; data: T; reason: null; detail: null }
  | {
      ok: false
      status: number
      data: null
      reason: string | null
      detail: string | null
    }

export interface AdoptInput {
  kind: MarkKind
  method: MarkMethod
  image: Blob
  typedText?: string
}

export interface FinishResult {
  signed: boolean
  envelope_status: string
}

export interface SigningTransport {
  getState(): Promise<Outcome<SigningState>>
  getDocument(): Promise<Outcome<Uint8Array>>
  /** Only a door that can be sent a notice of cancellation reads one. */
  getNotice?(): Promise<Outcome<Uint8Array>>
  downloadNotice?(filename: string): Promise<Outcome<unknown>>
  download(filename: string): Promise<Outcome<unknown>>
  downloadCertificate(filename: string): Promise<Outcome<unknown>>
  adopt(input: AdoptInput): Promise<Outcome<unknown>>
  getAdoptedMark(kind: MarkKind): Promise<Outcome<Blob>>
  completeField(fieldId: string, value?: string): Promise<Outcome<unknown>>
  finish(): Promise<Outcome<FinishResult>>
  decline(reason: string): Promise<Outcome<unknown>>
  pageViewed(page: number): Promise<void>
}

// The date signed and the full name are written by the server.
export const FILLED_FOR_YOU: ReadonlySet<FieldType> = new Set(["date_signed", "full_name"])

export const isOnNotice = (field: SigningField) =>
  field.document === "notice_of_cancellation"

export const needsSigner = (field: SigningField) => !FILLED_FOR_YOU.has(field.field_type)
